/**
 * Utility functions for converting territory polygon data to Google Maps format.
 * Supports both legacy format (array of coordinates) and new format with holes.
 *
 * @see https://github.com/medscout-dev/medscout-api/pull/3046
 */

import polygonClipping, { Polygon as PCPolygon } from 'polygon-clipping';

/**
 * Type guard to check if a polygon is in the new format with holes.
 * New format: { boundary: Coordinate[], holes: Coordinate[][] }
 * Legacy format: Coordinate[]
 */
export function isPolygonWithHoles(
  polygon: MedScout.TerritoryPolygon
): polygon is MedScout.PolygonWithHoles {
  return (
    typeof polygon === 'object' &&
    polygon !== null &&
    'boundary' in polygon &&
    'holes' in polygon &&
    Array.isArray(polygon.boundary) &&
    Array.isArray(polygon.holes)
  );
}

/**
 * Returns true if a coordinate has finite lat and lng values.
 */
function isValidCoordinate(p: MedScout.Coordinate): boolean {
  return Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

/**
 * Converts a territory polygon to Google Maps LatLng paths format.
 * Google Maps supports holes by passing multiple paths:
 * - First path: outer boundary (exterior ring)
 * - Subsequent paths: holes (interior rings)
 *
 * @param polygon - Territory polygon in either legacy or new format
 * @returns Array of LatLng arrays suitable for google.maps.Polygon paths
 */
export function convertPolygonToMapPaths(
  polygon: MedScout.TerritoryPolygon
): google.maps.LatLng[][] {
  if (isPolygonWithHoles(polygon)) {
    const paths: google.maps.LatLng[][] = [];

    // Add boundary as first path
    const boundary = polygon.boundary.filter(isValidCoordinate);
    if (boundary.length > 0) {
      paths.push(
        boundary.map((p) => new google.maps.LatLng(p.lat, p.lng))
      );
    }

    // Add holes as subsequent paths (only if they're valid arrays)
    polygon.holes.forEach((hole) => {
      if (Array.isArray(hole)) {
        const validHole = hole.filter(isValidCoordinate);
        if (validHole.length > 0) {
          paths.push(validHole.map((p) => new google.maps.LatLng(p.lat, p.lng)));
        }
      }
    });

    return paths;
  }

  // Legacy format - just an array of coordinates
  // Add defensive check in case polygon is not an array
  if (!Array.isArray(polygon)) {
    return [];
  }
  const validCoords = polygon.filter(isValidCoordinate);
  if (validCoords.length === 0) return [];
  return [validCoords.map((p) => new google.maps.LatLng(p.lat, p.lng))];
}

/**
 * Gets the boundary coordinates from a polygon (for bounds calculation).
 * For new format, returns just the boundary (holes are inside, so not needed for bounds).
 * For legacy format, returns the polygon itself.
 *
 * @param polygon - Territory polygon in either legacy or new format
 * @returns Array of coordinates representing the outer boundary
 */
export function getPolygonBoundaryCoords(
  polygon: MedScout.TerritoryPolygon
): MedScout.Coordinate[] {
  if (isPolygonWithHoles(polygon)) {
    return polygon.boundary;
  }
  // Legacy format - return polygon if it's an array, otherwise empty array
  return Array.isArray(polygon) ? polygon : [];
}

/**
 * Converts all polygons from a territory to Google Maps format.
 * Handles multi-polygon territories (e.g., Hawaii with multiple islands).
 *
 * @param polygons - Array of territory polygons
 * @returns Flat array of all paths for use with google.maps.Polygon
 */
export function convertTerritoryPolygonsToMapPaths(
  polygons: MedScout.TerritoryPolygon[]
): google.maps.LatLng[][] {
  if (!Array.isArray(polygons)) {
    return [];
  }
  return polygons.flatMap((polygon) => convertPolygonToMapPaths(polygon));
}

/**
 * Gets a copy of the boundary coordinates from a polygon.
 * Useful when you need to mutate the coordinates (e.g., closing a polygon).
 *
 * @param polygon - Territory polygon in either legacy or new format
 * @returns A new array of coordinates (safe to mutate)
 */
export function getPolygonBoundaryCopy(
  polygon: MedScout.TerritoryPolygon
): MedScout.Coordinate[] {
  const coords = getPolygonBoundaryCoords(polygon);
  return [...coords];
}

/**
 * Converts territory polygons to legacy format for API submission.
 * Since the write API doesn't change, we need coordinates in the old format.
 *
 * @param polygons - Array of territory polygons (either format)
 * @returns Array of coordinate arrays in legacy format
 */
export function convertToLegacyPolygonFormat(
  polygons: MedScout.TerritoryPolygon[]
): MedScout.Coordinate[][] {
  if (!Array.isArray(polygons)) {
    return [];
  }
  return polygons.map((polygon) => getPolygonBoundaryCopy(polygon));
}

/**
 * Converts a territory polygon to GeoJSON coordinate format [lng, lat].
 * For new format with holes, returns array of rings (boundary first, then holes).
 * For legacy format, returns array with single ring.
 *
 * @param polygon - Territory polygon in either legacy or new format
 * @returns Array of coordinate rings in GeoJSON format [[lng, lat], ...][]
 */
export function convertPolygonToGeoJsonCoords(
  polygon: MedScout.TerritoryPolygon
): [number, number][][] {
  if (isPolygonWithHoles(polygon)) {
    const rings: [number, number][][] = [];

    // Add boundary as first ring
    rings.push(polygon.boundary.map((p) => [p.lng, p.lat]));

    // Add holes as subsequent rings (only if they're valid arrays)
    polygon.holes.forEach((hole) => {
      if (Array.isArray(hole)) {
        rings.push(hole.map((p) => [p.lng, p.lat]));
      }
    });

    return rings;
  }

  // Legacy format - single ring
  // Add defensive check in case polygon is not an array
  if (!Array.isArray(polygon)) {
    return [];
  }
  return [polygon.map((p) => [p.lng, p.lat])];
}

/**
 * Converts V2 boundary geometry to Google Maps LatLng paths.
 * V2 API returns geometry as [ring][point][lng, lat] (GeoJSON order).
 *
 * @param geometry - Array of rings, each ring is array of [lng, lat] points
 * @returns Array of LatLng arrays suitable for google.maps.Polygon paths
 */
export function convertBoundaryGeometryToMapPaths(
  geometry: number[][][]
): google.maps.LatLng[][] {
  if (!Array.isArray(geometry)) return [];
  return geometry
    .map((ring) =>
      ring
        .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng))
        .map(([lng, lat]) => new google.maps.LatLng(lat, lng))
    )
    .filter((ring) => ring.length > 0);
}

/**
 * Unions multiple V2 boundary geometries into a single merged shape.
 * Returns a MultiPolygon (array of polygons, each with rings).
 *
 * The V2 API's translate_geometry strips inner rings (holes) and returns only
 * outer rings. A geometry with multiple rings represents a MultiPolygon
 * (disconnected parts like islands), NOT a Polygon with holes. Each ring is
 * wrapped as a separate single-ring Polygon so polygon-clipping correctly
 * treats them all as outer boundaries to union together.
 *
 * @param geometries - Array of boundary geometry coordinate arrays from V2 API
 * @returns MultiPolygon coordinate array (merged shape)
 */
export function unionBoundaryGeometries(
  geometries: number[][][][]
): number[][][][] {
  if (geometries.length === 0) return [];

  // Wrap each ring as its own single-ring Polygon to avoid multi-ring
  // geometries being misinterpreted as [outer, hole, ...] by polygon-clipping.
  const polygons = geometries.flatMap((geometry) =>
    geometry.map((ring) => [ring])
  );

  if (polygons.length === 0) return [];
  if (polygons.length === 1) return [polygons[0]];

  return polygonClipping.union(
    polygons[0] as PCPolygon,
    ...(polygons.slice(1) as PCPolygon[])
  ) as number[][][][];
}
